import { useRADDispatch } from "../../RADHandler/contexts/RADDispatchContext.jsx";
import { useMessages } from "@cadenza/messages";
import { normalizeActivityForDomain } from "../../RADHandler/activityKind.js";

export const useDropLogic = (
  diagramRef,
  draggedItem,
  viewBoxWidth,
  viewBoxHeight
) => {
  const { addActivity, addRole } = useRADDispatch();
  const { success, error } = useMessages();

  const locateDroppedItem = () => {
    const svg = diagramRef.current.querySelector("svg");
    if (!svg) return;
    const gElem = svg.querySelector("g");

    // Convert the screen coordinates to g coordinates
    // Account for the window scroll position
    const x = draggedItem.x - window.scrollX;
    const y = draggedItem.y - window.scrollY;

    const pt = svg.createSVGPoint();
    pt.x = x;
    pt.y = y;
    const transformedPt = pt.matrixTransform(svg.getScreenCTM().inverse());

    // Check if the transformed point is within the diagram area
    if (
      transformedPt.x >= 0 &&
      transformedPt.y >= 0 &&
      transformedPt.x <= viewBoxWidth &&
      transformedPt.y <= viewBoxHeight
    ) {
      const gPt = svg.createSVGPoint();
      gPt.x = x;
      gPt.y = y;
      const transformedGPt = gPt.matrixTransform(
        gElem.getScreenCTM().inverse()
      );
      return { transformedGPt, transformedPt };
    } else {
      return { undefined };
    }
  };

  const addDroppedItem = (dropped_item) => {
    // Guards!
    if (!dropped_item || !dropped_item.class) {
      error("Drop action failed: No item or item class provided");
      return;
    }
    // Get the element at the drop point
    const elementAtDrop = document.elementFromPoint(
      dropped_item.x,
      dropped_item.y
    );

    // Locate the dropped item.
    const { transformedGPt } = locateDroppedItem();
    if (!transformedGPt) {
      error("Drop must be on the drawing area");
      return; // No drop target found
    }
    if (!elementAtDrop || !elementAtDrop.closest("svg")) {
      // The drop point is not on the SVG
      error("Drop must be on the diagram area");
      return;
    }
    if (
      dropped_item.class === "role" &&
      elementAtDrop.classList.contains("role")
    ) {
      error("Cannot drop role on another role");
      return;
    }
    // End of guards
    const { class: dropped_item_class, type: dropped_item_type } = dropped_item;
    const normalizedActivity = normalizeActivityForDomain({
      classname: dropped_item_class,
      type: dropped_item_type,
      position: { x: transformedGPt.x, y: transformedGPt.y },
    });

    if (dropped_item_class === "role") {
      // Add a new role
      const position = transformedGPt;
      const result = addRole({ position });

      if (result?.success) {
        success(result.message);
      } else {
        error(result?.message || "Failed to add role");
      }
      return;
    }

    const groupElement = elementAtDrop.closest("g");
    if (!groupElement) {
      error("No valid target found at drop point");
      return;
    }

    const targetId = groupElement ? groupElement.getAttribute("data-id") : null;
    const targetClass = groupElement
      ? groupElement.getAttribute("data-class")
      : null;
    const targetType = groupElement
      ? groupElement.getAttribute("data-type")
      : null;

    console.log(
      `Adding dropped item of class 
          ${dropped_item_class}.${dropped_item_type}, 
          TARGET, ${targetId}, ${targetClass}.${targetType}`
    );

    if (["activity", "refinement"].includes(dropped_item_class)) {
      if (
        ["role", "thread", "activity", "refinement"].includes(targetClass) ||
        ["condition", "partThread", "repeat"].includes(targetType)
      ) {
        // Add an activity to the RAD.
        const result = addActivity({
          target: { id: targetId, classname: targetClass, type: targetType },
          activity: normalizedActivity,
        });

        if (result?.success) {
          success(result.message);
        } else {
          error(result?.message || "Failed to add activity");
        }
      } else if (targetClass === "state") {
        // Add an activity to the state
        // States are anonymous in themselves, so look up the tree for the
        // thread this is part of.
        // Look for a subthread group first, then a thread group.
        const threadGroupElement =
          elementAtDrop.closest("g.subthread-group") ||
          elementAtDrop.closest("g.thread-group");
        if (!threadGroupElement) {
          error("Cannot find parent thread for state");
          return;
        }
        const thread_id = threadGroupElement.getAttribute("data-id");
        const result = addActivity({
          target: {
            id: targetId,
            classname: targetClass,
            type: targetType,
            parentThreadId: thread_id,
          },
          activity: normalizeActivityForDomain({
            ...normalizedActivity,
            position: undefined,
          }),
        });

        if (result?.success) {
          success(result.message);
        } else {
          error(result?.message || "Failed to add activity to state");
        }
      } else {
        error(`Cannot add ${dropped_item_class} to ${targetClass}`);
      }
    } else {
      error(`Invalid drop target for ${dropped_item_class}`);
    }
  };
  return { locateDroppedItem, addDroppedItem };
};
